import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advanced from 'dayjs/plugin/advancedFormat'
import { ASSET_RETURNS_TITLE } from './constants'
import { Role } from '../types'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advanced)

export const concatClassName = (classNames?: string[]) => {
  let concatClass = ''
  classNames &&
    classNames.map((className) => {
      concatClass = concatClass.concat(className).concat(' ')
    })
  return concatClass
}

export const stringToArray = (string: string) => {
  if (!string) return
  return string.split(';')
}

export const createSlug = (slug: string) => slug.toLowerCase().replace(/[^\w-]+/g, '-')

export const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
export const numberRegex = /^[1-9]\d*(\.\d+)?$/
export const calculateRemainingDays = (startDate: Date, endDate: Date): number => {
  // const startDate = Date.now()
  if (startDate.getTime() < endDate.getTime())
    return Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
  else return 0
}

export const convertToNumber = (input: string): number => {
  let convertedToNumber = ''
  for (let i = 0; i < input.length; i++) {
    const hasNumber = /\d/
    if (hasNumber.test(input[i]) || input[i] === '.') {
      convertedToNumber += input[i]
    }
  }
  if (convertedToNumber.length > 0) return parseFloat(convertedToNumber)
  else return 0
}

export const convertNumberWithCommas = (input: string | number): string => {
  const val = typeof input === 'string' ? convertToNumber(input) : input
  return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const displayShares = (shares: string | number | undefined): string => {
  if (!shares || shares === '0' || shares === 'null') {
    return '0 shares'
  }

  const shareCount = convertToNumber(shares.toString())

  if (shareCount >= 1000000) {
    return `${(shareCount / 1000000).toFixed(1)}M shares`
  } else if (shareCount >= 10000) {
    return `${(shareCount / 1000).toFixed(1)}K shares`
  } else {
    return `${shareCount.toLocaleString()} shares`
  }
}

export const displayShareCount = (shares: string | number | undefined): string => {
  if (!shares || shares === '0' || shares === 'null') {
    return '0'
  }

  const shareCount = convertToNumber(shares.toString())

  if (shareCount >= 1000000) {
    return `${(shareCount / 1000000).toFixed(1)}M`
  } else if (shareCount >= 10000) {
    return `${(shareCount / 1000).toFixed(1)}K`
  } else {
    return shareCount.toLocaleString()
  }
}

export const calculateCommittedPercentage = (
  committedAmount: string,
  raiseGoal?: string,
  assetClass?: string,
  assetClassData?: any,
): string => {
  try {
    let goal: number

    // For Listed Equity, use total_shares from asset_class_data
    if (assetClass === 'Listed Equity' && assetClassData?.total_shares) {
      goal = convertToNumber(assetClassData.total_shares.toString())
    } else {
      // For other asset classes, use raise_goal
      if (!raiseGoal || raiseGoal === '0' || raiseGoal === 'null') {
        return ''
      }
      goal = convertToNumber(raiseGoal.toString())
    }

    const amount = convertToNumber(committedAmount.toString())
    const remainingGoal = (amount * 100) / goal
    return remainingGoal.toFixed(2).toString().concat('%')
  } catch (err) {
    return '0%'
  }
}

export const calculatePercentage = (a: string, b: string) => {
  try {
    const first = convertToNumber(a.toString())
    const second = convertToNumber(b.toString())
    return (first * 100) / second
  } catch (err) {
    return 0
  }
}

export const roundPercentage = (percentage: number | string) => {
  try {
    return Math.floor(convertToNumber(percentage.toString()))
  } catch (err) {
    return 0
  }
}

export const isValidFileSize = (file: File, type: 'image' | 'doc') => {
  /* 1024 = 1MB */
  const size = Math.round(file.size / 1024)

  if (type === 'doc' && size <= 2 * 1024 * 10) {
    return true
  } else return type === 'image' && size <= 1024 * 10
}

export const isValidImageType = (file: File) => {
  const extension = file.name.substr(file.name.lastIndexOf('.'))

  /* define allowed file types */
  const allowedExtensionsRegx = /(\.jpg|\.jpeg)$/i

  /* testing extension with regular expression */
  return allowedExtensionsRegx.test(extension)
}

export const isValidDocType = (file: File) => {
  const extension = file.name.substr(file.name.lastIndexOf('.'))

  /* define allowed file types */
  const allowedExtensionsRegx = /(\.docx|\.pdf)$/i

  /* testing extension with regular expression */
  return allowedExtensionsRegx.test(extension)
}

export const truncateNumber = (num: number, places = 2) => {
  return Math.trunc(num * Math.pow(10, places)) / Math.pow(10, places)
}

export const displayPercentage = (value: string): string =>
  `${
    Number.isInteger(Number(value)) ? parseFloat(value).toFixed(0) : parseFloat(value).toFixed(2)
  }%`

export const displayDollar = (
  value: string | undefined,
  currencyCode: string | undefined,
  maximumFractionDigits = 2,
  forceStandardFormat = false,
  forceFractionDigits = false,
) => {
  const oneBillion = 1000000000
  const oneMillion = 1000000
  const oneThousand = 1000
  const dollar = value && parseFloat(value) / 100
  let output = 0

  // Integer cents are always whole numbers; only strip fraction digits when the
  // caller did not ask to keep them (wallet / transactional balances pass true).
  if (Number.isInteger(Number(value)) && !forceFractionDigits) maximumFractionDigits = 0

  const fractionDigitsOpts = forceFractionDigits
    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    : { maximumFractionDigits }

  if (dollar && dollar >= oneBillion && !forceStandardFormat) {
    output = truncateNumber(dollar / oneBillion)
    return `${output.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      maximumFractionDigits: maximumFractionDigits,
    })}B`
  } else if (dollar && dollar >= oneMillion && !forceStandardFormat) {
    output = truncateNumber(dollar / oneMillion)
    // For millions, allow at least 1 decimal place to avoid rounding issues (e.g., 2.5M showing as $3M)
    const millionFractionDigits =
      output % 1 !== 0 ? Math.max(1, maximumFractionDigits) : maximumFractionDigits
    return `${output.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      maximumFractionDigits: millionFractionDigits,
    })}M`
  } else if (dollar && dollar >= oneThousand && !forceStandardFormat) {
    // Use proper rounding for better precision (e.g., $101.15k)
    output = Math.round((dollar / oneThousand) * 100) / 100
    const thousandFractionDigits = Math.max(2, maximumFractionDigits)
    return `${output.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      maximumFractionDigits: thousandFractionDigits,
    })}K`
  }
  return dollar
    ? `${dollar.toLocaleString('en-US', {
        style: 'currency',
        currency: currencyCode || 'USD',
        ...fractionDigitsOpts,
      })}`
    : '$0'
}

export const displayTicketSize = (value: string | undefined, currencyCode: string | undefined) => {
  if (!value) return '$0'

  const oneBillion = 1000000000
  const oneMillion = 1000000
  const oneThousand = 1000
  const dollar = parseFloat(value) / 100

  if (dollar >= oneBillion) {
    const output = Math.round((dollar / oneBillion) * 100) / 100
    const fractionDigits = output % 1 === 0 ? 0 : 2
    return `${output.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })}B`
  } else if (dollar >= oneMillion) {
    const output = Math.round((dollar / oneMillion) * 100) / 100
    const fractionDigits = output % 1 === 0 ? 0 : 2
    return `${output.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })}M`
  } else if (dollar >= oneThousand) {
    const output = Math.round((dollar / oneThousand) * 100) / 100
    const fractionDigits = output % 1 === 0 ? 0 : 2
    return `${output.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })}K`
  }

  const fractionDigits = dollar % 1 === 0 ? 0 : 2
  return `${dollar.toLocaleString('en-US', {
    style: 'currency',
    currency: currencyCode || 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`
}

export const displayOpportunityAmount = (
  value: string | undefined,
  currencyCode: string | undefined,
  assetClass: string | undefined,
  assetClassData: any,
  maximumFractionDigits = 2,
  forceStandardFormat = false,
  forceFractionDigits = false,
) => {
  // For Listed Equity with total_shares, the value represents share count
  if (assetClass === 'Listed Equity' && assetClassData?.total_shares) {
    // Display shares with currency format for opportunity amounts
    const oneBillion = 1000000000
    const oneMillion = 1000000
    const oneThousand = 1000
    const shareCount = value && parseFloat(value)
    let output = 0

    if (!shareCount) return '$0'

    if (shareCount >= oneBillion && !forceStandardFormat) {
      output = Math.round((shareCount / oneBillion) * 100) / 100
      const billionFractionDigits = Math.max(2, maximumFractionDigits)
      return `${output.toLocaleString('en-US', {
        style: 'currency',
        currency: currencyCode || 'USD',
        maximumFractionDigits: billionFractionDigits,
      })}B`
    } else if (shareCount >= oneMillion && !forceStandardFormat) {
      output = Math.round((shareCount / oneMillion) * 100) / 100
      const millionFractionDigits =
        shareCount % oneMillion !== 0 ? Math.max(1, maximumFractionDigits) : maximumFractionDigits
      return `${output.toLocaleString('en-US', {
        style: 'currency',
        currency: currencyCode || 'USD',
        maximumFractionDigits: millionFractionDigits,
      })}M`
    } else if (shareCount >= oneThousand && !forceStandardFormat) {
      output = Math.round((shareCount / oneThousand) * 100) / 100
      const thousandFractionDigits = Math.max(2, maximumFractionDigits)
      return `${output.toLocaleString('en-US', {
        style: 'currency',
        currency: currencyCode || 'USD',
        maximumFractionDigits: thousandFractionDigits,
      })}K`
    }

    return shareCount.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      maximumFractionDigits: maximumFractionDigits,
    })
  }

  // For all other cases, use regular dollar display (cents to dollars)
  return displayDollar(
    value,
    currencyCode,
    maximumFractionDigits,
    forceStandardFormat,
    forceFractionDigits,
  )
}

export const arrayToString = (array: string[]) => {
  if (!array) return
  return array.join(';')
}

export const removeEmptyValues = (obj: object) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v))

export const trimText = (text: string, charCount = 100) => {
  return text.length <= charCount ? text : text.substring(0, charCount).concat('...')
}

/** Title-cases each whitespace-delimited word (e.g. "LISTED EQUITY" → "Listed Equity"). */
export const formatTitleCaseWords = (value: string): string => {
  if (!value) return ''
  return value
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const stringIfNull = (value: string | null) => {
  if (!value) return ''
  return value
}

export const displayStatus = (status: string) => {
  if (!status) return ''
  const lower = status.toLowerCase()
  const capitalize = lower[0].toUpperCase() + lower.slice(1)
  return capitalize.replace(/_/g, ' ').replace(/-/g, ' ')
}

export const getFileKeyFromSignedUrl = (signedUrl: string): string => {
  if (signedUrl.startsWith('http')) {
    const regexToExtractKey = /^.*\/([^/]+?)\?.*$/
    const match = signedUrl.match(regexToExtractKey)
    return match && match[1] ? match[1] : signedUrl
  }
  return signedUrl
}

export const setPreviousUrl = (url: string) => {
  const store = window.localStorage
  store.setItem('prevUrl', url)
}

export const getPreviousUrl = (): string => {
  const store = window.localStorage
  const prevUrl = store.getItem('prevUrl')
  if (prevUrl) return prevUrl
  return ''
}

export const clearPreviousUrl = (): void => {
  window.localStorage.removeItem('prevUrl')
}

export const formatDate = (date: Date) => {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]

  if (!(date instanceof Date)) {
    throw new Error('Invalid input: expected a Date object.')
  }

  const year = date.getFullYear()
  const month = months[date.getMonth()].toUpperCase()
  const day = date.getDate()

  return `${year} ${month}, ${day}`
}

export const isMultiple = (x: number, y: number) => (x % y === 0 ? true : false)
export const truncateString = (text: string, maxLength: number) => {
  if (text.length > maxLength) {
    return `${text.slice(0, maxLength - 3)}...`
  }
  return text
}

export const convertDateToDayjs = (date: Date | undefined | null) => {
  return dayjs(date)
}

export const displayReturnsTitle = (assetClass?: string) => {
  return assetClass ? ASSET_RETURNS_TITLE[assetClass] : 'IRR'
}

export const formatTargetReturn = (
  minimumReturn?: string,
  maximumReturn?: string,
  investmentReturn?: string,
) => {
  const minVal = minimumReturn ? parseFloat(minimumReturn) : null
  const maxVal = maximumReturn ? parseFloat(maximumReturn) : null

  // Priority 1: Show range if both min and max are provided and different
  if (minVal !== null && maxVal !== null && minVal !== maxVal) {
    return `${minVal}–${maxVal}% p.a.`
  }

  // Priority 2: Show single value if min is provided (max might be same or null)
  if (minVal !== null) {
    return `${minVal}% p.a.`
  }

  // Priority 3: Show max if only max is provided
  if (maxVal !== null) {
    return `${maxVal}% p.a.`
  }

  // Priority 4: Fallback to legacy investment_return for existing opportunities
  if (investmentReturn) {
    const returnVal = parseFloat(investmentReturn)
    return `${returnVal}% p.a.`
  }

  return '0% p.a.'
}

export const getError = (errorObj: Date | Array<string> | object | string) => {
  if (errorObj instanceof Date) {
    return errorObj.toString()
  }
  if (Array.isArray(errorObj)) {
    return errorObj.length > 0 ? errorObj[0] : ''
  }
  if (typeof errorObj === 'object' && errorObj !== null && !(errorObj instanceof Function)) {
    return JSON.stringify(errorObj)
  }
  return errorObj
}

export const getFormattedDate = (date: Date, format: string): string => {
  return dayjs(date).format(format)
}

export const convertToURLSearchParams = (obj: { [key: string]: string }) => {
  const params = new URLSearchParams()

  for (const key in obj) {
    params.append(key, obj[key])
  }

  return params
}

export const calculateStartDateFromFilter = (filterValue: string): string => {
  const today = new Date()
  const [num, unit] = filterValue.split(' ')
  const value = parseInt(num)

  if (unit === 'day') {
    today.setDate(today.getDate() - value)
  } else if (unit === 'month') {
    today.setMonth(today.getMonth() - value)
  }

  return today.toLocaleDateString('en-CA')
}

export const getTimestampWithTimezone = (date: Date, format: string): string => {
  // Case for showing timezone abbreviation
  if (format.endsWith('zzz')) {
    const dateString = dayjs(date)
      .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
      .format(format)

    const regex = /(?:(.+?)\s+)?(\d{2}:\d{2}:\d{2})\s+(.*)/
    const match = dateString.match(regex)

    if (match) {
      // Extract part before timezone
      const dateTime = match.slice(1, 3).join(' ')

      // Extract timezone name (e.g., 'India Standard Time')
      const timezoneName = match[3].trim()

      // Converting to timezone abbreviation
      const timezoneAbbr = timezoneName
        .split(' ')
        .reduce((acc: string, word: string) => acc + word[0].toUpperCase(), '')

      if (timezoneAbbr) {
        return `${dateTime} ${timezoneAbbr}`
      }
      // If not found, return the original timezone name
      else {
        return `${dateTime} ${timezoneName}`
      }
    }
  }

  return dayjs(date).tz(Intl.DateTimeFormat().resolvedOptions().timeZone).format(format)
}

export const getRoleString = (role: Role) => {
  switch (role) {
    case Role.credibilaAdmin:
      return 'admin'
    case Role.investor:
      return 'investor'
    case Role.superuser:
      return 'superuser'
    case Role.credibilaDev:
      return 'dev'

    default:
      return 'superuser'
  }
}

export const capitalizeText = (text: string) => text?.[0].toUpperCase() + text.slice(1)
